// Браузерная часть событий карточек /tutors (02.11.2026): очередь в памяти,
// пачка раз в ~5 с и на pagehide / уход вкладки в фон — через sendBeacon.
// Ошибки отправки молча игнорируются: событие теряется, страница не замечает.
//
// Показы — один общий IntersectionObserver на все карточки: карточка видна
// ≥ 50 % — одно событие на (место, анкета) за загрузку страницы. Повторная
// прокрутка, смена фильтров и перерисовка списка второго события не дают.
import { MAX_EVENTS_PER_CALL, type EventPlacement, type PartnerEvent } from "@/lib/map-partner-events";

const URL_EVENTS = "/api/map/events";
const FLUSH_MS = 5000;

type Queued = { partner_id: string; event: PartnerEvent; placement: EventPlacement; page: string };

const queue: Queued[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let hooked = false;

function send(batch: Queued[], beacon: boolean) {
  const body = JSON.stringify({ events: batch });
  try {
    if (beacon && navigator.sendBeacon?.(URL_EVENTS, body)) return;
    void fetch(URL_EVENTS, { method: "POST", body, keepalive: true, headers: { "content-type": "text/plain" } }).catch(() => undefined);
  } catch {
    /* молча */
  }
}

function flush(beacon = false) {
  if (timer) clearTimeout(timer);
  timer = null;
  while (queue.length) send(queue.splice(0, MAX_EVENTS_PER_CALL), beacon);
}

function hook() {
  if (hooked || typeof window === "undefined") return;
  hooked = true;
  window.addEventListener("pagehide", () => flush(true));
  document.addEventListener("visibilitychange", () => document.visibilityState === "hidden" && flush(true));
}

/** Событие в очередь; отправка — пачкой через ~5 с или при уходе со страницы. */
export function logPartnerEvent(partnerId: string, event: PartnerEvent, placement: EventPlacement) {
  if (typeof window === "undefined") return;
  hook();
  queue.push({ partner_id: partnerId, event, placement, page: window.location.pathname });
  if (queue.length >= MAX_EVENTS_PER_CALL) flush();
  else if (!timer) timer = setTimeout(() => flush(), FLUSH_MS);
}

/** Уже засчитанные показы за эту загрузку: «place:id». */
const seen = new Set<string>();
let io: IntersectionObserver | null = null;

function observer(): IntersectionObserver | null {
  if (io || typeof IntersectionObserver === "undefined") return io;
  io = new IntersectionObserver(
    (entries) => {
      for (const e of entries) {
        if (!e.isIntersecting || e.intersectionRatio < 0.5) continue;
        const el = e.target as HTMLElement;
        const id = el.dataset.evPid;
        const place = el.dataset.evPlace as EventPlacement | undefined;
        if (!id || !place) continue;
        io?.unobserve(el);
        const key = `${place}:${id}`;
        if (seen.has(key)) continue;
        seen.add(key);
        logPartnerEvent(id, place === "featured" ? "featured_impression" : "card_impression", place);
      }
    },
    { threshold: 0.5 },
  );
  return io;
}

/**
 * ref-колбэк карточки: следить за показом. Карточка уже засчитана — не следим.
 * Возвращает уборку (React 19 вызывает её при размонтировании).
 */
export function watchImpression(el: HTMLElement | null, partnerId: string, place: EventPlacement) {
  if (!el || seen.has(`${place}:${partnerId}`)) return;
  const o = observer();
  if (!o) return;
  el.dataset.evPid = partnerId;
  el.dataset.evPlace = place;
  o.observe(el);
  return () => o.unobserve(el);
}
